import { type ReactNode, useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export type ConfirmOptions = {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  /** Red confirm button, for actions that remove, refund or end something. */
  destructive?: boolean;
  /** The confirm button stays off until this exact text is typed. For irreversible actions. */
  typeToConfirm?: string;
};

type ConfirmDialogProps = ConfirmOptions & {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/** The admin console's "are you sure?" dialog, in place of window.confirm. */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  destructive,
  typeToConfirm,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState("");
  useEffect(() => {
    if (open) setTyped("");
  }, [open]);
  const ready = !typeToConfirm || typed.trim() === typeToConfirm;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent {...(description ? {} : { "aria-describedby": undefined })}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription asChild><div>{description}</div></DialogDescription>}
        </DialogHeader>
        {typeToConfirm && (
          <label className="block space-y-1.5 text-sm text-slate-700">
            <span>
              Type <code className="rounded bg-slate-100 px-1 font-mono">{typeToConfirm}</code> to
              confirm
            </span>
            <Input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && ready) onConfirm();
              }}
            />
          </label>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            disabled={!ready}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * window.confirm as a dialog: `if (!(await confirm({ title }))) return;`. Render the returned
 * element once in the component.
 */
export function useConfirm() {
  const [pending, setPending] = useState<
    (ConfirmOptions & { resolve: (ok: boolean) => void }) | null
  >(null);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => setPending({ ...options, resolve })),
    []
  );

  const settle = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };

  const dialog = pending ? (
    <ConfirmDialog {...pending} open onCancel={() => settle(false)} onConfirm={() => settle(true)} />
  ) : null;

  return [confirm, dialog] as const;
}
